import ClickableLink from 'components/Common/Clickable/ClickableLink'
import Image from 'components/Common/Image/Image'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import PaletteSheet from 'LuxKit/PaletteSheet'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import config from 'constants/config'
import { isString } from 'lib/string/stringUtils'
import { rem } from 'polished'
import React, { useCallback, useContext } from 'react'
import styled from 'styled-components'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import clsx from 'clsx'
import { mediaHoverable } from 'lib/theme/mediaQueries'

export const CAROUSEL_CARD_WIDTH = 320

const CarouselCardContainer = styled(ClickableLink)`
  display: grid;
  grid-template-rows: ${rem(200)} 1fr;
  gap: ${rem(16)};
  height: 100%;
  position: relative;
  min-height: ${rem(520)};
`

const ImageContainer = styled(PaletteSheet)`
  position: relative;

  &.hover {
    > :nth-child(2) {
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      transition: opacity 0.3s;
      opacity: 0;
    }

    > :nth-child(1) {
      transition: opacity 0.3s;
      opacity: 1;
    }

    ${mediaHoverable} {
      &:hover {
        > :nth-child(1) {
          opacity: 0;
        }

        > :nth-child(2) {
          opacity: 1;
        }
      }
    }
  }
`

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: grid;
  grid-template-columns: auto auto;
  grid-template-areas: "left right";
  grid-template-rows: 1fr;
  padding: ${rem(8)} ${rem(12)};
  pointer-events: none;
  justify-content: space-between;

  & > .left {
    justify-self: flex-start;
    align-self: flex-start;
    grid-area: left;
  }

  & > .right {
    justify-self: flex-end;
    align-self: flex-start;
    grid-area: right;
  }

  a, button {
    pointer-events: auto;
  }
`

interface Props {
  className?: string;
  bookmarkButton?: React.ReactElement;
  description?: string | React.ReactElement;
  descriptionLines?: number;
  image: App.Image;
  location: string | React.ReactElement;
  title: string;
  priceDetails?: React.ReactElement;
  to: string;
  onClick?: () => void;
  productType?: App.ProductType;
  productTypeKind?: 'default' | 'plain';
  rating?: App.OfferRating
  urgencyLabels?: React.ReactElement;
  partnershipLabel?: React.ReactElement;
  'data-testid'?: string;
  loyaltyPointsElement?: React.ReactElement;
  imageCornerLabel?: React.ReactElement;
  hoverImage?: App.Image;
}

function CarouselCardSmall(props: Props) {
  const {
    bookmarkButton,
    description,
    descriptionLines,
    image,
    hoverImage,
    location,
    title,
    priceDetails,
    to,
    onClick,
    productType,
    productTypeKind,
    rating,
    urgencyLabels,
    partnershipLabel,
    className,
    'data-testid': dataTestId,
    loyaltyPointsElement,
    imageCornerLabel,
  } = props

  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const handleImageLoaded = useCallback(() => {
    dispatchOfferListEvent({ type: OfferListEvents.offerReady })
  }, [dispatchOfferListEvent])

  const isDesktop = useScreenSizeUp('tablet')
  const isHoverEnabled = !!useOptimizelyExperiment(OptimizelyExperiments.croChangeImageOnHover, isDesktop && !!hoverImage)

  return <CarouselCardContainer
    data-testid={dataTestId}
    onClick={onClick}
    target={config.OPEN_NEW_TAB_OFFER_CLICK ? '_blank' : undefined}
    to={to}
    className={className}
  >
    <ImageContainer
      className={clsx({ hover: isHoverEnabled })}
      paletteType="default"
    >
      <Image
        aspectRatio="3:2"
        image={image}
        fit="center"
        dpr={2}
        height="200"
        width="320"
        onLoad={handleImageLoaded}
      />
      {hoverImage && isHoverEnabled && (
        <Image
          aspectRatio="3:2"
          image={hoverImage}
          fit="center"
          dpr={2}
          height="200"
          width="320"
        />
      )}
      <ImageOverlay>
        <LabelGroup className="left">{partnershipLabel}</LabelGroup>
        {imageCornerLabel}
        <TripGuard>
          <div className="right">
            {bookmarkButton}
          </div>
        </TripGuard>
      </ImageOverlay>
    </ImageContainer>
    <Group direction="vertical" verticalAlign="space-between" gap={16}>
      <Group direction="vertical" horizontalAlign="start" gap={8}>
        {urgencyLabels}
        <Group direction="vertical" gap={4}>
          {isString(location) ? <Caption colour="neutral-one" variant="large">{location}</Caption> : location}
          <Heading lineClamp={3} variant="heading6">{title}</Heading>
        </Group>
        {productType && <ProductTypeLabel productType={productType} kind={productTypeKind ?? 'plain'} />}
        {rating && <OfferRating
          variant="xs"
          rating={rating}
          inlineLabel
          hideLink
          onlyNumber="auto"
        />}
        {description && isString(description) && <BodyText colour="neutral-two" lineClamp={descriptionLines || 2} variant="medium">{description}</BodyText>}
        {description && !isString(description) && description}
        {loyaltyPointsElement}
      </Group>
      <div>
        {priceDetails}
      </div>
    </Group>
  </CarouselCardContainer>
}

export default CarouselCardSmall
